import PropTypes from 'prop-types';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import Posts from '../../components/Posts/Posts';
import Heading from '../../components/Heading/Heading';
import Documents from '../../components/Documents/Documents';
import ButtonLink from '../../components/Shared/ButtonLink';
import CTACard from '../../components/CTACard/CTACard';
import EmailSignup from '../../components/Shared/EmailSignup';
import CTAVideoSeries from '../../components/CTAVideoSeries/CTAVideoSeries';
import { getPageContent } from '../../services/dpc';
import { getBlogPosts } from '../../services/blogService';
import Error from '../../components/Error/Error';
import PostFeatured from '../../components/PostFeatured/PostFeatured';
import PostsSmall from '../../components/PostsSmall/PostsSmall';
import PopularFAQs from '../../components/PopularFAQs/PopularFAQs';
import CalculatorsLarge from '../../components/CalculatorsLarge/CalculatorsLarge';
import { slugByLabel } from '../../util/faqCategories';
import useGTM from '../../hooks/useGTM';
import VideoSection from '../../components/VideoSection/VideoSection';
import createGTMClass from '../../util/createGTMClass';
import theme from '../../styles/theme';

const faqGTMLabels = {
  'filing-damage-claims': 'file_claim',
  'what-to-do-with-insurance-claim-checks': 'claim_check',
  'requesting-loan-payoff-quote': 'payoff_quote',
  'update-homeowners-policy-information': 'update_policy',
};

function Resources({
  data: {
    contentCTAs,
    contentVideos,
    faqPod,
    metaDescription,
    tipsAndToolsPod,
    titleTag,
    pageTitle,
    webForm,
  } = {},
  blogData,
  error,
  isLoading,
}) {
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }

  const [triggerGTMEvent] = useGTM();
  const featuredPosts =
    tipsAndToolsPod &&
    tipsAndToolsPod.map(
      ({
        title,
        contentTag,
        category,
        slug,
        featuredMedia,
        copyBrief: excerpt,
      }) => ({
        title,
        contentTag,
        category,
        thumbnail: `${featuredMedia?.media?.fields?.file?.url}`,
        slug,
        excerpt,
        mediaType: featuredMedia?.mediaType,
      })
    );

  const posts =
    blogData &&
    blogData.map(({ title, contentTag, category, slug, featuredMedia }) => ({
      title,
      category,
      contentTag,
      thumbnail: `${
        featuredMedia?.media?.fields?.file?.url ||
        featuredMedia?.thumbnail?.fields?.file?.url
      }?w=300`,
      mediaType: featuredMedia?.mediaType,
      slug,
    }));

  const faqs =
    faqPod &&
    faqPod.map(({ faqQuestion: title, slug: url, faqCategory }) => ({
      title,
      url: url.replace(
        'resources/faqs/',
        `resources/faqs/${slugByLabel(faqCategory?.[0])}/`
      ),
      onClick: triggerGTMEvent({
        name: 'vmf.cta_click',
        location: 'Resources',
        category: 'frequently_asked',
        action: 'summary',
        label:
          faqGTMLabels[
            url.replace('/resources/faqs/', '').replace(/\/$/, '')
          ] ||
          url
            .replace('/resources/faqs/', '')
            .replace(/-/g, '_')
            .replace(/\/$/, ''),
      }),
    }));

  return (
    <Layout isLoading={isLoading}>
      <SEO title={titleTag} description={metaDescription} />

      <Heading background={theme.colors.blue10} appearance='dark'>
        <h1>{pageTitle}</h1>
      </Heading>

      <PostFeatured
        {...featuredPosts?.[0]}
        onImageClick={triggerGTMEvent({
          name: 'vmf.cta_click',
          location: 'Resources',
          category: 'loan_education',
          action: 'image',
          label: 'article_1',
        })}
        onLinkClick={triggerGTMEvent({
          name: 'vmf.cta_click',
          location: 'Resources',
          category: 'loan_education',
          action: 'text',
          label: 'article_1',
        })}
        linkClassName={createGTMClass('guide', 'hero', 'text')}
      />

      <PostsSmall posts={featuredPosts ? featuredPosts.slice(1, 4) : []} />

      <Posts
        title='Helpful Resources from the Consumer Financial Protection Bureau'
        posts={featuredPosts ? featuredPosts.slice(4) : []}
      />

      <hr
        style={{
          borderTop: 'solid 1px #e4e5e7',
          marginTop: '-100px',
          marginBottom: '50px',
          width: '80%',
        }}
      />

      <CTAVideoSeries
        title='Watch our Home Tips video series for home care ideas.'
        link={{
          to: '/resources/home-care-video-tips/',
          size: 'small',
          onClick: triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Resources',
            category: 'loan_education',
            action: 'button',
            label: 'home_tips',
          }),
        }}
      />

      <Documents>
        <h2>{contentCTAs?.[0]?.ctaHeader}</h2>
        <p>{contentCTAs?.[0]?.ctaIntro}</p>
        <section className='links'>
          <ButtonLink
            to={
              contentCTAs?.[0]?.firstCTAButtonUrl ||
              '/resources/documents-and-forms'
            }
            appearance='dark'
            className={createGTMClass('view_all_docs', 'cta', 'button')}
          >
            {contentCTAs?.[0].firstCTAButtonText || 'View all documents'}
          </ButtonLink>
        </section>
      </Documents>

      <PopularFAQs
        title='Popular FAQs'
        link={{
          to: '/resources/faqs/',
          title: 'View all',
          onClick: triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Resources',
            category: 'frequently_asked',
            action: 'button',
            label: 'faqs',
          }),
        }}
        faqs={faqs}
        borders={true}
      >
        <p>
          As you take the home buying journey and move into homeownership, we
          know you have questions along the way! We have the answer to the
          mortgage questions you might be asking.
        </p>
      </PopularFAQs>

      <CalculatorsLarge />

      <Posts
        marginBottomAdjustment={'90px'}
        title='Tips & Tools'
        link={{
          title: 'View All',
          to: '/resources/blog/',
          onClick: triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Resources',
            category: 'loan_education',
            action: 'button',
            label: 'tips_tools',
          }),
        }}
        posts={posts}
      />

      <VideoSection
        id='resources_video'
        style={{ paddingTop: '200px', marginTop: '-200px' }}
        video={{
          source: contentVideos?.[0]?.videoSource,
          content: contentVideos?.[0]?.embeddedContent,
          onClick: triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Home Page',
            category: 'loan_education',
            action: 'video',
            label: 'watch_video_2',
          }),
        }}
        title='Understanding Your Monthly Statement'
      />

      <CTACard
        form={
          <EmailSignup
            buttonLabel={webForm?.[0]?.firstFormButtonText}
            originCampaign={webForm?.[0]?.originCampaign}
            originWebApp={webForm?.[0]?.originWebApp}
            originWebFormCategory={webForm?.[0]?.originWebFormCategory}
            originWebFormType={webForm?.[0]?.originWebFormType}
            originWebPageType={webForm?.[0]?.originWebPageType}
            dataExtension={webForm?.[0]?.dataExtension}
            className={createGTMClass('subscribe', 'prefoot_cta', 'button')}
            onEmailSubmit={triggerGTMEvent({
              name: 'vmf.cta_click',
              location: 'Resources',
              category: 'contact_us',
              action: 'form',
              label: 'email_form_submit',
            })}
          />
        }
        disclaimer={
          <ReactMarkdown
            source={webForm?.[0]?.disclaimer}
            renderers={{
              paragraph: ({ children }) => (
                <p className='type-caption bold color-grey1'>{children}</p>
              ),
            }}
          />
        }
        divider={false}
      >
        <p className='type-label-large bold'>{webForm?.[0]?.formIntro}</p>
        <h3 className='type-h1'>{webForm?.[0]?.formHeader}</h3>
      </CTACard>
    </Layout>
  );
}

Resources.getInitialProps = async ({}) => {
  try {
    const dpc = await getPageContent('/resources/');
    const blogData = await getBlogPosts('all', '', 0, 8, ['News']);

    if (dpc.status !== 200) {
      throw dpc.statusText;
    }

    if (blogData.status !== 200) {
      throw blogData.statusText;
    }

    return {
      data: dpc.data.result,
      blogData: blogData.data.result,
    };
  } catch (error) {
    return {
      error,
    };
  }
};

Resources.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default Resources;
